import React, { useMemo } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Grid,
  Box,
  Card,
  Typography,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  useGetallStudentQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import { ITeam, IStudent } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { Link } from "react-router-dom";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import GroupIcon from "@mui/icons-material/Group";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SchoolIcon from "@mui/icons-material/School";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import BadgeIcon from "@mui/icons-material/Badge";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

interface ReportCard {
  title: string;
  description: string;
  link: string;
  icon: React.ReactNode;
  color: string;
}

const studentReports: ReportCard[] = [
  {
    title: "Student Enrollment",
    description: "View enrollment statistics and trends",
    link: "/report/student-enrollment",
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    color: "#1976d2",
  },
  {
    title: "Student Performance",
    description: "Academic performance overview",
    link: "/report/student-performance",
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    color: "#388e3c",
  },
];

const attendanceReports: ReportCard[] = [
  {
    title: "Daily Attendance",
    description: "View daily attendance records",
    link: "/report/daily-attendance",
    icon: <EventAvailableIcon sx={{ fontSize: 40 }} />,
    color: "#f57c00",
  },
  {
    title: "Attendance Summary",
    description: "Monthly and yearly attendance summary",
    link: "/report/attendance-summary",
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    color: "#7b1fa2",
  },
];

const paymentReports: ReportCard[] = [
  {
    title: "Payment History",
    description: "View all payment transactions",
    link: "/report/payment-history",
    icon: <PaymentIcon sx={{ fontSize: 40 }} />,
    color: "#00796b",
  },
  {
    title: "Revenue Report",
    description: "Monthly revenue breakdown",
    link: "/report/revenue",
    icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
    color: "#c62828",
  },
];

const staffReports: ReportCard[] = [
  {
    title: "Staff Directory",
    description: "Complete staff listing and details",
    link: "/report/staff-directory",
    icon: <BadgeIcon sx={{ fontSize: 40 }} />,
    color: "#455a64",
  },
  {
    title: "Staff Attendance",
    description: "Staff attendance and punctuality",
    link: "/report/staff-attendance",
    icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    color: "#5d4037",
  },
];

const Index = () => {
  const classes = useStyles();
  const { showErrorMessage } = useNotifier();

  const { data: studentData, isLoading: studentLoading } = useGetallStudentQuery({
    searchText: "",
    id: "",
  });

  const { data: staffData, isLoading: staffLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const students: IStudent[] = useMemo(() => {
    return Array.isArray(studentData?.data) ? (studentData?.data as IStudent[]) : [];
  }, [studentData?.data]);

  const staff: ITeam[] = useMemo(() => {
    return Array.isArray(staffData?.data) ? (staffData?.data as ITeam[]) : [];
  }, [staffData?.data]);

  const totalStudents = students.length;
  const totalStaff = staff.length;

  const pageIsLoading = studentLoading || staffLoading;

  if (pageIsLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
    );

  const renderStatCard = (
    title: string,
    value: string | number,
    icon: React.ReactNode,
    color: string
  ) => (
    <Card
      className={classes.card}
      sx={{
        height: "100%",
        borderTop: `3px solid ${color}`,
      }}
    >
      <CardContent>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
              {value}
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ color: color, opacity: 0.7 }}>{icon}</Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderReportSection = (sectionTitle: string, reports: ReportCard[]) => (
    <Grid item xs={12}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {sectionTitle}
      </Typography>
      <Grid container spacing={2}>
        {reports.map((report) => (
          <Grid item xs={12} sm={6} md={3} key={report.title}>
            <Link to={report.link} style={{ textDecoration: "none" }}>
              <Card
                className={classes.card}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Box sx={{ color: report.color, mb: 1 }}>{report.icon}</Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {report.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {report.description}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      {/* Summary Stats */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              "Total Students",
              totalStudents,
              <PeopleAltIcon sx={{ fontSize: 40 }} />,
              "#1976d2"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              "Total Staff",
              totalStaff,
              <GroupIcon sx={{ fontSize: 40 }} />,
              "#388e3c"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              "Monthly Revenue",
              "--",
              <AttachMoneyIcon sx={{ fontSize: 40 }} />,
              "#f57c00"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              "Attendance Rate",
              "--",
              <EventAvailableIcon sx={{ fontSize: 40 }} />,
              "#7b1fa2"
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* Report Sections */}
      {renderReportSection("Student Reports", studentReports)}
      {renderReportSection("Attendance Reports", attendanceReports)}
      {renderReportSection("Payment Reports", paymentReports)}
      {renderReportSection("Staff Reports", staffReports)}
    </Grid>
  );
};

export default Index;
